import React from 'react';
import { formatDate } from './formatDate';
import { formatAnalyticalValue } from './formatValue';
import { getResolvedValue } from './resolveColumns';
import type { ResolvedEntityMap } from './resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';
import type { Entity } from '../types';

export interface RenderCellOptions {
  onEntityClick?: (entityId: number) => void;
  onNavigate?: (path: string) => void;
  resolvedEntities?: ResolvedEntityMap;
}

/**
 * Walk a dot-separated path into a JSON blob, unwrapping { value: X } objects.
 */
function getExtractedValue(entity: Entity, col: ColumnDef): any {
  const extract = col.extract!;
  const blob = entity.attributes?.[extract.from];
  if (!blob || typeof blob !== 'object') return undefined;
  let current: any = blob;
  for (const segment of extract.path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[segment];
  }
  if (current != null && typeof current === 'object' && 'value' in current) {
    current = current.value;
  }
  return current;
}

/**
 * Extract a plain string value for a cell (for CSV export, no React nodes).
 */
export function getCellPlainValue(
  entity: Entity,
  col: ColumnDef,
  resolvedEntities: ResolvedEntityMap = new Map(),
): string {
  if (col.key === 'id') return entity.id != null ? String(entity.id) : '';

  if (col.key.includes('.')) {
    const value = getResolvedValue(entity, col, resolvedEntities);
    if (value === null || value === undefined) return '';
    if (col.render === 'date' && (typeof value === 'string' || typeof value === 'number')) return formatDate(value) || '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  if (col.extract) {
    return formatAnalyticalValue(getExtractedValue(entity, col));
  }

  const value = entity.attributes?.[col.key];
  if (value === null || value === undefined) return '';
  if (col.render === 'date') return formatDate(value) || '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function renderCellValue(
  entity: Entity,
  col: ColumnDef,
  options: RenderCellOptions = {},
): React.ReactNode {
  const { onEntityClick, onNavigate, resolvedEntities = new Map() } = options;

  if (col.key === 'id') {
    return onEntityClick && entity.id ? (
      <span
        className="entity-id"
        onClick={(e) => { e.stopPropagation(); onEntityClick(entity.id!); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick(entity.id!); }
        }}
      >
        {entity.id}
      </span>
    ) : (
      <span>{entity.id}</span>
    );
  }

  if (col.key.includes('.')) {
    const value = getResolvedValue(entity, col, resolvedEntities);
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }
    if (col.render === 'date' && (typeof value === 'string' || typeof value === 'number')) return formatDate(value) || '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  if (col.extract) {
    const extracted = getExtractedValue(entity, col);
    if (extracted === undefined) return <span className="null-value">-</span>;
    return formatAnalyticalValue(extracted);
  }

  const value = entity.attributes?.[col.key];

  if (value === null || value === undefined) {
    return <span className="null-value">-</span>;
  }

  if (col.render === 'link' && typeof value === 'number') {
    const targetType = col.key.replace(/_id$/, '');
    return (
      <span
        className="entity-id"
        onClick={onNavigate ? (e) => { e.stopPropagation(); onNavigate(`/${targetType}/${value}`); } : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={onNavigate ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(`/${targetType}/${value}`); }
        } : undefined}
      >
        {value}
      </span>
    );
  }

  if (col.render === 'date') {
    return formatDate(value) || '-';
  }

  if (col.render === 'state' && typeof value === 'string') {
    const lower = value.toLowerCase();
    let cls = '';
    if (lower === 'completed') cls = 'fv-state-completed';
    else if (lower === 'in_progress' || lower === 'in progress') cls = 'fv-state-progress';
    else if (lower === 'cancelled' || lower === 'failed') cls = 'fv-state-cancelled';
    else cls = 'fv-state-default';
    return <span className={`fv-state-badge ${cls}`}>{value}</span>;
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
}
